import React from 'react'
import Text from '../../Text'
import { $green10, $green20, $green70 } from '../../../styles/colors/primitives'
import styles from './styles.module.css'

// Short catalog-driven pill for a shop card (shopItem doc field `cardTag`, e.g.
// "Complete setup" on the GT-2 full-rig bundle). Anchored TOP-LEFT like
// EndOfLifeTag, because the right-hand slot is already the FreeShipping /
// PreOrder / out-of-stock stack.
//
// Two looks, picked by the catalog field `cardTagVariant`:
//  - (default) faint green: an "everything included" reassurance that must not
//    compete with the red discount percentage on the same card.
//  - "fire": orange-to-VRS-red gradient with a flickering flame and a soft
//    pulsing glow, for the one hero bundle we want to run hot. Animation is off
//    under prefers-reduced-motion (see the stylesheet).
function FlameIcon() {
  return (
    <svg className={styles.flame} viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M8.6 1.2c.3 2.1-.4 3.5-1.6 4.6-.5-.8-.6-1.7-.4-2.5C4.3 5 3 7 3 9.2A5 5 0 0 0 8 14a5 5 0 0 0 5-4.8c0-3.3-2.2-6-4.4-8zM8 12.6a2.2 2.2 0 0 1-2.2-2.2c0-1.1.7-1.9 1.6-2.6.1.6.4 1.1.9 1.4.9-.6 1.3-1.4 1.3-2.3.9.9 1.5 2 1.5 3.3A2.2 2.2 0 0 1 8 12.6z"
      />
    </svg>
  )
}

// Seasonal sale pills (lib/saleCampaign getSaleCardTag): "sale" = black with a
// red edge (every item in the sale), "sale-limited" = solid VRS red with a
// stopwatch (a limited time deal that still has units). A limited pill can carry
// a second, black segment (`sub`, e.g. "or until stock runs out").
// "muted" = neutral grey note (e.g. excluded from the Sim Expo promo).
// `offsetTop` (px) stacks a second pill under the first one in the same
// top-left slot (e.g. the Sim Expo pill under the clutch sale pill).
function ClockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M6 .5h4V2H8.75v1.05A6.5 6.5 0 1 1 7.25 3.05V2H6V.5Zm2 4a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm-.75 1.5h1.5v3.69l2.03 2.03-1.06 1.06-2.47-2.47V6Zm5.6-3.36 1.06-1.06 1.41 1.41-1.06 1.06-1.41-1.41Z"/>
    </svg>
  )
}

const VARIANT_CLASS = { fire: styles.fire, muted: styles.muted, sale: styles.sale, 'sale-limited': styles.saleLimited }

function CardTag({ label, sub = null, show, variant = null, offsetTop = 0, className = null }) {
  if (!label) return null
  const fire = variant === 'fire'
  const variantClass = VARIANT_CLASS[variant]
  if (variant === 'sale-limited' && sub) {
    return (
      <div
        className={[styles.tag, styles.split, show ? styles.shown : styles.hidden, className].filter(Boolean).join(' ')}
        style={offsetTop ? { top: `${offsetTop}px` } : undefined}
      >
        <span className={styles.splitMain}><ClockIcon/><Text type="tiny">{label}</Text></span>
        <span className={styles.splitSub}><Text type="tiny">{sub}</Text></span>
      </div>
    )
  }
  const style = variantClass ? {} : { background: $green10, color: $green70, borderColor: $green20 }
  if (offsetTop) style.top = `${offsetTop}px`
  return (
    <div
      className={[styles.tag, variantClass || styles.plain, show ? styles.shown : styles.hidden, className].filter(Boolean).join(' ')}
      style={Object.keys(style).length ? style : undefined}
    >
      {fire && <FlameIcon/>}
      {variant === 'sale-limited' && <ClockIcon/>}
      <Text type="tiny">{label}</Text>
    </div>
  )
}

export default CardTag
